/**
 * Geo-Tracking Service
 * Handles location tracking, geocoding, and consent management
 */

import { apiClient } from './apiClient';

// ============================================================================
// TYPES AND INTERFACES
// ============================================================================

export interface GeoConsentPreferences {
  id?: number;
  user_id: number;
  organization_id: number | null;
  address_geocoding_consent: boolean;
  site_usage_tracking_consent: boolean;
  specific_location_consent: boolean;
  workflow_tracking_consent: boolean;
  form_tracking_consent: boolean;
  allow_organizational_reporting: boolean;
  data_retention_days: number;
  consent_granted_at: string | null;
  consent_last_updated_at: string;
  consent_version: string;
}

export interface GeoLocation {
  id?: number;
  location_type: 'address' | 'gps' | 'ip_based';
  street_address?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  latitude?: number;
  longitude?: number;
  accuracy_meters?: number;
  geocoded_address?: string;
  geocoding_provider?: string;
  geocoding_confidence?: number;
  ip_address?: string;
  ip_location_city?: string;
  ip_location_region?: string;
  ip_location_country?: string;
}

export interface GeoTrackingEvent {
  id?: number;
  user_id: number;
  organization_id: number | null;
  event_type: string;
  event_category: 'usage' | 'workflow' | 'form' | 'address';
  geo_location_id?: number;
  related_member_id?: number;
  related_workflow_id?: number;
  related_workflow_step_id?: number;
  related_form_id?: number;
  related_enrollment_id?: number;
  event_description?: string;
  event_metadata?: Record<string, any>;
  consent_verified: boolean;
  consent_type?: string;
  session_id?: string;
  user_agent?: string;
  device_type?: string;
  browser?: string;
  os?: string;
  event_timestamp: string;
  duration_seconds?: number;
}

export interface GeocodeResult {
  latitude: number;
  longitude: number;
  formatted_address: string;
  confidence: number;
  provider: string;
}

export type ConsentType =
  | 'address_geocoding'
  | 'site_usage_tracking'
  | 'specific_location'
  | 'workflow_tracking'
  | 'form_tracking';

// ============================================================================
// GEO-TRACKING SERVICE CLASS
// ============================================================================

class GeoTrackingService {
  private sessionId: string;
  private cachedConsent: GeoConsentPreferences | null = null;
  private lastConsentCheck: number = 0;
  private readonly CONSENT_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  constructor() {
    this.sessionId = this.generateSessionId();
  }

  // ==========================================================================
  // SESSION MANAGEMENT
  // ==========================================================================

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
  }

  private getDeviceInfo() {
    const ua = navigator.userAgent;
    let device_type = 'desktop';
    if (/mobile/i.test(ua)) device_type = 'mobile';
    else if (/tablet|ipad/i.test(ua)) device_type = 'tablet';

    let browser = 'unknown';
    if (ua.includes('Chrome')) browser = 'Chrome';
    else if (ua.includes('Firefox')) browser = 'Firefox';
    else if (ua.includes('Safari')) browser = 'Safari';
    else if (ua.includes('Edge')) browser = 'Edge';

    let os = 'unknown';
    if (ua.includes('Windows')) os = 'Windows';
    else if (ua.includes('Mac')) os = 'macOS';
    else if (ua.includes('Linux')) os = 'Linux';
    else if (ua.includes('Android')) os = 'Android';
    else if (ua.includes('iOS')) os = 'iOS';

    return { device_type, browser, os, user_agent: ua };
  }

  // ==========================================================================
  // CONSENT MANAGEMENT
  // ==========================================================================

  /**
   * Get user's geo-tracking consent preferences
   */
  async getConsentPreferences(
    userId: number,
    organizationId?: number
  ): Promise<GeoConsentPreferences | null> {
    try {
      // Check cache first
      const now = Date.now();
      if (this.cachedConsent && now - this.lastConsentCheck < this.CONSENT_CACHE_TTL) {
        return this.cachedConsent;
      }

      const params = organizationId ? { organization_id: organizationId } : {};
      const response = await apiClient.get(`/api/geo-tracking/consent/${userId}`, { params });

      this.cachedConsent = response.data;
      this.lastConsentCheck = now;

      return response.data;
    } catch (error) {
      console.error('Error fetching geo consent preferences:', error);
      return null;
    }
  }

  /**
   * Update user's consent preferences
   */
  async updateConsentPreferences(
    userId: number,
    preferences: Partial<GeoConsentPreferences>
  ): Promise<GeoConsentPreferences | null> {
    try {
      const response = await apiClient.put(`/api/geo-tracking/consent/${userId}`, preferences);

      // Update cache
      this.cachedConsent = response.data;
      this.lastConsentCheck = Date.now();

      return response.data;
    } catch (error) {
      console.error('Error updating geo consent preferences:', error);
      throw error;
    }
  }

  /**
   * Check if user has consented to a specific type of tracking
   */
  async hasConsent(userId: number, consentType: ConsentType): Promise<boolean> {
    const preferences = await this.getConsentPreferences(userId);
    if (!preferences) return false;

    const consentMap: Record<ConsentType, keyof GeoConsentPreferences> = {
      address_geocoding: 'address_geocoding_consent',
      site_usage_tracking: 'site_usage_tracking_consent',
      specific_location: 'specific_location_consent',
      workflow_tracking: 'workflow_tracking_consent',
      form_tracking: 'form_tracking_consent',
    };

    const field = consentMap[consentType];
    return Boolean(preferences[field]);
  }

  /**
   * Clear cached consent (call when user updates preferences)
   */
  clearConsentCache(): void {
    this.cachedConsent = null;
    this.lastConsentCheck = 0;
  }

  // ==========================================================================
  // LOCATION TRACKING
  // ==========================================================================

  /**
   * Get user's current GPS location (requires specific_location consent)
   */
  async getCurrentLocation(): Promise<GeolocationPosition | null> {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        console.warn('Geolocation not supported by browser');
        resolve(null);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => resolve(position),
        (error) => {
          console.error('Error getting current location:', error);
          resolve(null);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000, // Cache for 5 minutes
        }
      );
    });
  }

  /**
   * Geocode an address to coordinates
   */
  async geocodeAddress(address: {
    street_address?: string;
    city?: string;
    state?: string;
    postal_code?: string;
    country?: string;
  }): Promise<GeocodeResult | null> {
    try {
      const response = await apiClient.post('/api/geo-tracking/geocode', address);
      return response.data;
    } catch (error) {
      console.error('Error geocoding address:', error);
      return null;
    }
  }

  /**
   * Get IP-based location (fallback method)
   */
  async getIPBasedLocation(): Promise<GeoLocation | null> {
    try {
      const response = await apiClient.get('/api/geo-tracking/ip-location');
      return response.data;
    } catch (error) {
      console.error('Error getting IP-based location:', error);
      return null;
    }
  }

  /**
   * Create a geo location record
   */
  async createGeoLocation(location: Partial<GeoLocation>): Promise<GeoLocation | null> {
    try {
      const response = await apiClient.post('/api/geo-tracking/locations', location);
      return response.data;
    } catch (error) {
      console.error('Error creating geo location:', error);
      return null;
    }
  }

  // ==========================================================================
  // EVENT TRACKING
  // ==========================================================================

  /**
   * Track a geo event with consent verification
   */
  async trackEvent(params: {
    userId: number;
    organizationId?: number;
    eventType: string;
    eventCategory: 'usage' | 'workflow' | 'form' | 'address';
    consentType: ConsentType;
    geoLocationId?: number;
    relatedMemberId?: number;
    relatedWorkflowId?: number;
    relatedWorkflowStepId?: number;
    relatedFormId?: number;
    relatedEnrollmentId?: number;
    description?: string;
    metadata?: Record<string, any>;
    durationSeconds?: number;
  }): Promise<GeoTrackingEvent | null> {
    try {
      // Verify consent
      const hasConsent = await this.hasConsent(params.userId, params.consentType);

      const deviceInfo = this.getDeviceInfo();

      const eventData: Partial<GeoTrackingEvent> = {
        user_id: params.userId,
        organization_id: params.organizationId,
        event_type: params.eventType,
        event_category: params.eventCategory,
        geo_location_id: params.geoLocationId,
        related_member_id: params.relatedMemberId,
        related_workflow_id: params.relatedWorkflowId,
        related_workflow_step_id: params.relatedWorkflowStepId,
        related_form_id: params.relatedFormId,
        related_enrollment_id: params.relatedEnrollmentId,
        event_description: params.description,
        event_metadata: params.metadata,
        consent_verified: hasConsent,
        consent_type: params.consentType,
        session_id: this.sessionId,
        ...deviceInfo,
        duration_seconds: params.durationSeconds,
      };

      const response = await apiClient.post('/api/geo-tracking/events', eventData);
      return response.data;
    } catch (error) {
      console.error('Error tracking geo event:', error);
      return null;
    }
  }

  /**
   * Track site usage (page visit)
   */
  async trackSiteUsage(
    userId: number,
    organizationId: number | undefined,
    pagePath: string,
    metadata?: Record<string, any>
  ): Promise<void> {
    // Check consent first
    const hasConsent = await this.hasConsent(userId, 'site_usage_tracking');
    if (!hasConsent) return;

    // Try to get IP-based location as fallback
    const ipLocation = await this.getIPBasedLocation();
    let geoLocationId: number | undefined;

    if (ipLocation?.id) {
      geoLocationId = ipLocation.id;
    }

    await this.trackEvent({
      userId,
      organizationId,
      eventType: 'site_visit',
      eventCategory: 'usage',
      consentType: 'site_usage_tracking',
      geoLocationId,
      description: `Visited ${pagePath}`,
      metadata: {
        ...metadata,
        page_path: pagePath,
        referrer: document.referrer,
      },
    });
  }

  /**
   * Track workflow step completion with location
   */
  async trackWorkflowStep(params: {
    userId: number;
    organizationId?: number;
    workflowId: number;
    workflowStepId?: number;
    memberId?: number;
    stepName: string;
    includeLocation?: boolean;
  }): Promise<void> {
    const hasConsent = await this.hasConsent(params.userId, 'workflow_tracking');
    if (!hasConsent) return;

    let geoLocationId: number | undefined;

    // If location tracking is enabled and consented
    if (params.includeLocation) {
      const hasLocationConsent = await this.hasConsent(params.userId, 'specific_location');
      if (hasLocationConsent) {
        const position = await this.getCurrentLocation();
        if (position) {
          const location = await this.createGeoLocation({
            location_type: 'gps',
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy_meters: position.coords.accuracy,
          });
          geoLocationId = location?.id;
        }
      }
    }

    await this.trackEvent({
      userId: params.userId,
      organizationId: params.organizationId,
      eventType: 'workflow_step_completion',
      eventCategory: 'workflow',
      consentType: 'workflow_tracking',
      geoLocationId,
      relatedWorkflowId: params.workflowId,
      relatedWorkflowStepId: params.workflowStepId,
      relatedMemberId: params.memberId,
      description: `Completed workflow step: ${params.stepName}`,
    });
  }

  /**
   * Track form submission with location
   */
  async trackFormSubmission(params: {
    userId: number;
    organizationId?: number;
    formId: number;
    memberId?: number;
    formName: string;
    includeLocation?: boolean;
  }): Promise<void> {
    const hasConsent = await this.hasConsent(params.userId, 'form_tracking');
    if (!hasConsent) return;

    let geoLocationId: number | undefined;

    if (params.includeLocation) {
      const hasLocationConsent = await this.hasConsent(params.userId, 'specific_location');
      if (hasLocationConsent) {
        const position = await this.getCurrentLocation();
        if (position) {
          const location = await this.createGeoLocation({
            location_type: 'gps',
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy_meters: position.coords.accuracy,
          });
          geoLocationId = location?.id;
        }
      }
    }

    await this.trackEvent({
      userId: params.userId,
      organizationId: params.organizationId,
      eventType: 'form_submission',
      eventCategory: 'form',
      consentType: 'form_tracking',
      geoLocationId,
      relatedFormId: params.formId,
      relatedMemberId: params.memberId,
      description: `Submitted form: ${params.formName}`,
    });
  }

  // ==========================================================================
  // DATA MANAGEMENT
  // ==========================================================================

  /**
   * Get user's geo tracking history
   */
  async getTrackingHistory(
    userId: number,
    filters?: {
      eventCategory?: string;
      startDate?: string;
      endDate?: string;
      limit?: number;
    }
  ): Promise<GeoTrackingEvent[]> {
    try {
      const response = await apiClient.get(`/api/geo-tracking/events/${userId}`, {
        params: filters,
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching tracking history:', error);
      return [];
    }
  }

  /**
   * Delete user's geo tracking history
   */
  async deleteTrackingHistory(userId: number): Promise<boolean> {
    try {
      await apiClient.delete(`/api/geo-tracking/events/${userId}`);
      return true;
    } catch (error) {
      console.error('Error deleting tracking history:', error);
      return false;
    }
  }

  /**
   * Delete specific tracking event
   */
  async deleteTrackingEvent(eventId: number): Promise<boolean> {
    try {
      await apiClient.delete(`/api/geo-tracking/events/single/${eventId}`);
      return true;
    } catch (error) {
      console.error('Error deleting tracking event:', error);
      return false;
    }
  }

  // ==========================================================================
  // ORGANIZATIONAL ANALYTICS (Admin only)
  // ==========================================================================

  /**
   * Get organizational geo analytics
   */
  async getOrganizationalAnalytics(
    organizationId: number,
    filters?: {
      startDate?: string;
      endDate?: string;
      eventCategory?: string;
    }
  ): Promise<any> {
    try {
      const response = await apiClient.get(`/api/geo-tracking/analytics/${organizationId}`, {
        params: filters,
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching organizational analytics:', error);
      return null;
    }
  }

  /**
   * Get consent status summary for organization
   */
  async getConsentStatusSummary(organizationId: number): Promise<any> {
    try {
      const response = await apiClient.get(
        `/api/geo-tracking/consent-summary/${organizationId}`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching consent summary:', error);
      return null;
    }
  }
}

// Export singleton instance
export const geoTracking = new GeoTrackingService();
